// Client for the OpenFGA **management** store — access-over-access for the Keycloak
// and OpenFGA modules. Talks to the dedicated, locked-down management API (store-bound:
// the store is fixed server-side, so no storeId is ever sent from here).
import { authorizedFetch } from "~/auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type {
  AuthorizationModel,
  CheckRequest,
  CheckResponse,
  ListModelsResponse,
  ReadTuplesRequest,
  ReadTuplesResponse,
  TupleKey,
} from "./fga-types";

const API_BASE = import.meta.env.VITE_MANAGEMENT_API_URL || "/management-api";

export interface AccessUserInfo {
  id: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
}

export interface AccessPrincipal {
  subject: string;
  subjectType: "user" | "client" | "group";
  access: "direct" | "indirect";
  via: string | null;
  info?: AccessUserInfo | null;
}

export interface ManagementAccessResponse {
  owner: AccessPrincipal[];
  maintainer: AccessPrincipal[];
  viewer: AccessPrincipal[];
}

export async function getManagementAccess(
  objectId: string,
): Promise<ManagementAccessResponse> {
  const res = await authorizedFetch(
    `${API_BASE}/access?object=${encodeURIComponent(objectId)}`,
  );
  await throwIfNotOk(res);
  return res.json();
}

export async function readManagementTuples(
  request: ReadTuplesRequest,
): Promise<ReadTuplesResponse> {
  const res = await authorizedFetch(`${API_BASE}/tuples`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwIfNotOk(res);
  return res.json();
}

/** A management role assignment a write superseded without the caller naming it (ADR-0038 §5). */
export interface ManagementSupersededGrant {
  object: string;
  role: string;
}

export interface ManagementWriteResponse {
  superseded: ManagementSupersededGrant[];
}

/**
 * Grant is a monotonic assignment (ADR-0038 §1): stronger over weaker promotes and reports what
 * it replaced; the same role is a no-op success; weaker over stronger is rejected with
 * `fga.role_downgrade_requires_demote` — use {@link demoteManagementRole} instead.
 */
export async function writeManagementTuple(
  tuple: TupleKey,
): Promise<ManagementWriteResponse> {
  const res = await authorizedFetch(`${API_BASE}/tuples/write`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ writes: [tuple] }),
  });
  await throwIfNotOk(res);
  return res.json();
}

export async function deleteManagementTuple(tuple: TupleKey): Promise<void> {
  const res = await authorizedFetch(`${API_BASE}/tuples/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deletes: [tuple] }),
  });
  await throwIfNotOk(res);
}

export interface ManagementDemoteRequest {
  user: string;
  relation: string;
  /** The role the caller believes is currently held — mandatory (ADR-0038 §2). */
  from: string;
  object: string;
}

/**
 * Lower a subject's direct role (ADR-0038 §2). `from` guards against a concurrent change: a
 * mismatched current role fails with `fga.role_assignment_mismatch` rather than overwriting it.
 */
export async function demoteManagementRole(
  request: ManagementDemoteRequest,
): Promise<ManagementWriteResponse> {
  const res = await authorizedFetch(`${API_BASE}/tuples/demote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwIfNotOk(res);
  return res.json();
}

export async function checkManagement(
  request: CheckRequest,
): Promise<CheckResponse> {
  const res = await authorizedFetch(`${API_BASE}/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwIfNotOk(res);
  return res.json();
}

export interface BatchCheckItem {
  tupleKey: TupleKey;
  correlationId: string;
}

export interface BatchCheckResponse {
  result: Record<string, { allowed: boolean }>;
}

export async function batchCheckManagement(
  checks: BatchCheckItem[],
): Promise<BatchCheckResponse> {
  const res = await authorizedFetch(`${API_BASE}/batch-check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ checks }),
  });
  await throwIfNotOk(res);
  return res.json();
}

export async function getActiveManagementModelId(): Promise<string | null> {
  const res = await authorizedFetch(`${API_BASE}/authorization-models/active`);
  await throwIfNotOk(res);
  const data = await res.json();
  return data.authorizationModelId ?? null;
}

export async function listManagementModels(): Promise<ListModelsResponse> {
  const res = await authorizedFetch(`${API_BASE}/authorization-models`);
  await throwIfNotOk(res);
  return res.json();
}

export async function getManagementModel(
  modelId: string,
): Promise<AuthorizationModel> {
  const res = await authorizedFetch(
    `${API_BASE}/authorization-models/${modelId}`,
  );
  await throwIfNotOk(res);
  return res.json();
}

export async function writeManagementModel(
  modelJson: string,
): Promise<{ authorizationModelId: string }> {
  const res = await authorizedFetch(`${API_BASE}/authorization-models`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ modelJson }),
  });
  await throwIfNotOk(res);
  return res.json();
}
